import { withDefault, definedMap } from "collection-utils";

import { SerializedComponent } from "../../components/SerializedApp";
import {
    ArrayScreenFormat,
    ScreenDescriptionKind,
    PropertyDescription,
    PropertyKind,
    TableGlideType,
    MutatingScreenKind,
} from "../description";
import {
    PropertyDescriptor,
    makeStringyColumnPropertyDescriptor,
    PropertyTableGetter,
    ComponentBuilder,
    ComponentConfiguratorContext,
} from "../components";
import { getColumnProperty, getEnumProperty, getSwitchProperty } from "../components/property-utils";
import {
    SummaryArrayContentDescription,
    SummaryArrayScreenDescription,
    SummaryArrayScreenHandler,
} from "./summary-array-screen";
import { ActionSource, QuotaBannerRequirements } from "../../components/types";
import { ListItemFlags } from "./list-item-flags";
import { MapListMode } from "../components/map-list-mode";
import { PropertySection } from "../components/property-sections";
import { makeCaptionPropertyDescriptor } from "../components/descriptor-utils";
import { InlineListBuild, makeEmptyMessage } from "./array-content";
import { LetBinding } from "../serialized-component";
import { getPropertyWithColumnFormat } from "../formulas";
import { EnumPropertyHandler } from "../components/property-handlers";
import { MapVisualType } from "./map-screen-types";

interface MapScreenContentDescription extends SummaryArrayContentDescription {
    readonly locationProperty: PropertyDescription;
    readonly captionProperty: PropertyDescription;
    readonly defaultModeProperty: PropertyDescription | undefined;
    readonly allowUserLocationProperty: PropertyDescription | undefined;
    readonly visualTypeProperty: PropertyDescription | undefined;
}

interface MapScreenDescription extends SummaryArrayScreenDescription, MapScreenContentDescription {
    readonly kind: ScreenDescriptionKind.Array;
    readonly format: ArrayScreenFormat.Map;
}

const visualTypePropertyHandler = new EnumPropertyHandler(
    { visualTypeProperty: MapVisualType.Street },
    "Visual type",
    "Visual type",
    [
        {
            label: "Street",
            value: MapVisualType.Street,
        },
        {
            label: "Satellite",
            value: MapVisualType.Satellite,
        },
    ],
    PropertySection.Design,
    "dropdown"
);

export class MapScreenHandler extends SummaryArrayScreenHandler<MapScreenContentDescription, MapScreenDescription> {
    protected readonly supportsNonURLImages = false;
    protected readonly supportsEmojiImages = false;
    protected readonly supportsTruncateList = false;

    constructor() {
        super(ArrayScreenFormat.Map, "Map", "map", true);
    }

    public get quotaBannerRequirements(): QuotaBannerRequirements {
        return { needListQuota: true, needMapQuota: true };
    }

    protected content(
        builder: ComponentBuilder,
        desc: MapScreenContentDescription,
        table: TableGlideType,
        arrayData: SerializedComponent,
        title: SerializedComponent | undefined,
        subtitle: SerializedComponent | undefined,
        image: SerializedComponent | undefined,
        onTap: SerializedComponent | undefined,
        onDropImage: SerializedComponent | undefined,
        searchActiveFlag: SerializedComponent | undefined,
        inlineListBuild: InlineListBuild | undefined,
        uniqueID: string,
        _caption: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined
    ): SerializedComponent {
        const { b } = builder;

        const locationProperty = getColumnProperty(desc.locationProperty);
        if (locationProperty === undefined) return b.errorUI;

        const defaultModeProperty = withDefault(getEnumProperty(desc.defaultModeProperty), MapListMode.Map);
        const visualType = visualTypePropertyHandler.getEnum(desc);

        const allowUserLocationProperty = withDefault(getSwitchProperty(desc.allowUserLocationProperty), false);

        const markers = b.map(
            arrayData,
            b.object({
                location: b.lookupGeoAddress(b.getProperty(locationProperty, b.getContext, table), uniqueID),
                title,
                subtitle,
                image,
                itemID: this.getItemID(b, b.getContext, table),
            })
        );

        const itemCaption = definedMap(getColumnProperty(desc.captionProperty), n =>
            getPropertyWithColumnFormat(builder, table, n)
        );

        const initials = title !== undefined ? b.extractInitials(title) : undefined;

        const lets: LetBinding[] = [];
        if (onTap !== undefined) {
            lets.push(["onTap", b.holdAction(onTap)]);
            onTap = b.getVariable("onTap");
        }

        return b.let(
            lets,
            b.ui("MapScreenList", {
                list: arrayData,
                markers,
                item: b.adaptiveListItem(
                    title,
                    subtitle,
                    itemCaption,
                    image,
                    undefined,
                    initials,
                    onTap,
                    onDropImage,
                    ActionSource.ListItem,
                    ListItemFlags.DisableChevron |
                        ListItemFlags.SwapImageSide |
                        ListItemFlags.LargeImage |
                        ListItemFlags.DrawSeparator,
                    undefined,
                    undefined,
                    itemID
                ),
                searchActive: searchActiveFlag,
                emptyMessage: makeEmptyMessage(b, searchActiveFlag, inlineListBuild),
                defaultMode: b.constant(defaultModeProperty),
                insideInlineList: b.constant(inlineListBuild !== undefined),
                allowUserLocation: b.constant(allowUserLocationProperty),
                onTap,
                visualType: b.constant(visualType),
            })
        );
    }

    public getBasicSearchProperties(desc: MapScreenContentDescription): readonly string[] {
        const props = super.getBasicSearchProperties(desc);
        const captionColumn = getColumnProperty(desc.captionProperty);
        if (captionColumn === undefined) {
            return props;
        } else {
            return [...props, captionColumn];
        }
    }

    public getContentPropertyDescriptors<T extends MapScreenContentDescription>(
        getPropertyTable: PropertyTableGetter<T> | undefined,
        insideInlineList: boolean,
        desc: T,
        ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined,
        isDefaultArrayScreen: boolean,
        withTransforms: boolean
    ): ReadonlyArray<PropertyDescriptor> {
        const descriptors = super.getContentPropertyDescriptors(
            getPropertyTable,
            insideInlineList,
            desc,
            ccc,
            mutatingScreenKind,
            isDefaultArrayScreen,
            withTransforms
        );
        return [
            makeStringyColumnPropertyDescriptor(
                "locationProperty",
                "Address",
                ["required", "editable", "searchable"],
                PropertySection.Data,
                getPropertyTable,
                ["location", "address"]
            ),
            ...descriptors,
            makeCaptionPropertyDescriptor(getPropertyTable, false),
            {
                kind: PropertyKind.Enum,
                property: { name: "defaultModeProperty" },
                label: "Default Screen",
                menuLabel: "Default view",
                cases: [
                    {
                        value: MapListMode.Map,
                        label: "Map",
                        icon: "mapView",
                    },
                    {
                        value: MapListMode.List,
                        label: "List",
                        icon: "listView",
                    },
                ],
                defaultCaseValue: MapListMode.Map,
                section: PropertySection.Design,
                visual: "small-images",
            },
            {
                kind: PropertyKind.Switch,
                property: { name: "allowUserLocationProperty" },
                label: "Allow user to show current location",
                section: PropertySection.Options,
                defaultValue: true,
            },
            visualTypePropertyHandler,
        ];
    }
}
